import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import CarouselEditorDialog from "../components/distribution/CarouselEditorDialog";
import {
  carouselFindingText,
  saveErrorText,
} from "../components/distribution/findings";
import { friendlyError } from "../utils/apiError";
import { foyerApi, type CarouselHolder, type CarouselRow } from "../http/foyer";
import { carouselWriteBody, type Carousel } from "@shared/carousel";
import { tokens } from "../theme";

/**
 * The carousel catalogue (BE-28). Figma `NendaPortal_1` node `459-3113`.
 *
 * ## Why this list is not paginated
 *
 * Production holds ~128 carousels, hand-authored. The server sends all of them
 * and the footer ("Showing 10 of 128") is a client-side reveal, exactly as
 * the design draws it. That also means search and the type filter are instant
 * and honest — they run over the whole set rather than over the page in hand,
 * which is the lie the Distribution lists had to be corrected out of.
 *
 * ## What the columns are, and what one of them is NOT
 *
 * `Name` · `Languages` (derived from `title[].language`) · `Display limit` ·
 * `Availability` · `Type`.
 *
 * The Figma mock is in Swedish; the CMS ships in English like every other page
 * in it, so the design's Swedish is the designer's working language rather than
 * a product decision.
 *
 * ⚠️ **`Availability` is `showNonPurchasedContent`, not a date.** The mockup's
 * rows 2–10 show `2026-05-23` under that heading; that is a copy-paste error in
 * the mock and row 1 ("Visar allt") is the correct row — confirmed by Sigge,
 * 2026-08-06. **There is no availability date on a carousel.** Availability is
 * derived per room from entitlement, and the only publish flag anywhere in this
 * model is `SubPage.draft`. A rights window belongs to the asset (BE-7).
 */

const TYPE_LABELS: Record<string, string> = {
  curated: "Manual",
  query: "Dynamic collection",
  epg: "Schedule (EPG)",
  recommended: "Recommended (not rendered)",
  continue_watching: "Continue watching (not rendered)",
};

const PAGE_STEP = 10;

export default function CarouselsPage() {
  const [rows, setRows] = useState<CarouselRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE_STEP);

  const [dialog, setDialog] = useState<{ editing: CarouselRow | null } | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{
    code?: string;
    message: string;
  } | null>(null);

  const [rowMenu, setRowMenu] = useState<{
    anchor: HTMLElement;
    row: CarouselRow;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CarouselRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [holders, setHolders] = useState<CarouselHolder[] | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const page = await foyerApi.listCarousels();
      setRows(page.carousels);
    } catch (e) {
      const err = e as { code?: string };
      // Never an empty table on a failure: "the server is down" must not read
      // as "there are no carousels".
      setRows(null);
      setError(saveErrorText(err.code, friendlyError(e)));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.name ?? "").toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        (r.title ?? []).some((t) => (t.text ?? "").toLowerCase().includes(q))
    );
  }, [rows, query]);

  const submit = async (draft: Carousel) => {
    setSaving(true);
    setSaveError(null);
    try {
      // ONE list, in `@shared/carousel`, covered by a test that fails when a
      // field goes missing. It was inline here, with a comment saying every
      // editor-settable field must be present — and `page` was added to the
      // model, the editor, the route, the store and the wire mapper but not to
      // this object, so the toggle never left the browser.
      const body = carouselWriteBody(draft);
      if (dialog?.editing) {
        await foyerApi.updateCarousel(dialog.editing.id, body);
      } else {
        await foyerApi.createCarousel(body);
      }
      setDialog(null);
      // Re-read rather than merging locally: the server derives `languages` and
      // `issues` on every read, and a merged row would carry the verdicts of
      // the payload we sent rather than of the row that now exists.
      await load();
    } catch (e) {
      const err = e as { code?: string };
      setSaveError({ code: err.code, message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  };

  const openDelete = async (row: CarouselRow) => {
    setConfirmDelete(row);
    setDeleteError(null);
    setHolders(null);
    try {
      // Ask BEFORE the operator commits, so the refusal is information rather
      // than a wall.
      const usage = await foyerApi.carouselUsage(row.id);
      setHolders(usage.holders);
    } catch {
      setHolders(null);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleteError(null);
    try {
      await foyerApi.deleteCarousel(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      const err = e as { code?: string };
      setDeleteError(saveErrorText(err.code, friendlyError(e)));
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Carousels"
        subtitle="Every row a guest can see on the home page. Authored once, globally; selected and ordered per room type."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSaveError(null);
              setDialog({ editing: null });
            }}
            data-testid="carousels-create"
          >
            Create carousel
          </Button>
        }
      />

      <Card sx={{ p: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2.5 }}
        >
          <TextField
            size="small"
            placeholder="Search carousels…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShown(PAGE_STEP);
            }}
            sx={{ width: 340 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{ "aria-label": "Search carousels" }}
          />
        </Stack>

        {rows === null && !error && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={22} />
          </Stack>
        )}

        {error && (
          <Stack spacing={1.5} alignItems="flex-start" sx={{ p: 3 }}>
            <Typography sx={{ color: tokens.error }}>{error}</Typography>
            <Button size="small" onClick={() => void load()}>
              Retry
            </Button>
          </Stack>
        )}

        {rows !== null && !error && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Languages</TableCell>
                    <TableCell>Display limit</TableCell>
                    <TableCell>Availability</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell width={48} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.slice(0, shown).map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setSaveError(null);
                        setDialog({ editing: row });
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>
                            {row.name || row.id}
                          </Typography>
                          {row.issues.length > 0 && (
                            <Tooltip
                              title={row.issues
                                .map(carouselFindingText)
                                .join(" ")}
                            >
                              <WarningIcon
                                sx={{ fontSize: 16, color: tokens.warning }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {row.languages.length === 0 ? (
                          <Typography
                            sx={{ color: tokens.inkFaint, fontSize: 13 }}
                          >
                            —
                          </Typography>
                        ) : (
                          row.languages.map((l) => l.toUpperCase()).join(", ")
                        )}
                      </TableCell>
                      <TableCell>
                        {/* A stored 0 is treated as unset everywhere else, so
                            showing "0" here would contradict the rail. */}
                        {row.displayLimit ? row.displayLimit : "10 (default)"}
                      </TableCell>
                      <TableCell>
                        {row.showNonPurchasedContent === false
                          ? "Purchased only"
                          : "Shows everything"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={TYPE_LABELS[row.type] ?? row.type}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          aria-label={`Actions for ${row.name || row.id}`}
                          onClick={(e) =>
                            setRowMenu({ anchor: e.currentTarget, row })
                          }
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography
                          sx={{ color: tokens.inkFaint, py: 3 }}
                          align="center"
                        >
                          {query
                            ? "No carousel matches that search."
                            : "No carousels yet."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ p: 2.5, borderTop: `1px solid ${tokens.divider}` }}
            >
              <Typography sx={{ color: tokens.inkFaint, fontSize: 13 }}>
                Showing {Math.min(shown, filtered.length)} of {filtered.length}
              </Typography>
              {shown < filtered.length && (
                <Button
                  size="small"
                  onClick={() => setShown((n) => n + PAGE_STEP)}
                >
                  Show more
                </Button>
              )}
            </Stack>
          </>
        )}
      </Card>

      <Menu
        open={Boolean(rowMenu)}
        anchorEl={rowMenu?.anchor}
        onClose={() => setRowMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (rowMenu) {
              setSaveError(null);
              setDialog({ editing: rowMenu.row });
            }
            setRowMenu(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenu) void openDelete(rowMenu.row);
            setRowMenu(null);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete “{confirmDelete?.name}”?</DialogTitle>
        <DialogContent>
          {holders === null ? (
            <Typography sx={{ color: tokens.inkFaint, fontSize: 13.5 }}>
              Checking where this carousel is used…
            </Typography>
          ) : holders.length === 0 ? (
            <Typography sx={{ fontSize: 13.5 }}>
              Nothing is showing this carousel. Deleting it cannot be undone.
            </Typography>
          ) : (
            <Stack spacing={1}>
              <Typography sx={{ fontSize: 13.5 }}>
                It is still shown by:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                {holders.map((h) => (
                  <li key={`${h.kind}:${h.id}`}>
                    <Typography sx={{ fontSize: 13.5 }}>
                      {h.name || h.id} (
                      {h.kind === "room-type" ? "room type" : "sub-page"})
                    </Typography>
                  </li>
                ))}
              </Box>
              <Typography sx={{ color: tokens.inkFaint, fontSize: 12.5 }}>
                Remove it from those first. Deleting it here is refused — it
                would leave behind an id that neither draws anything nor reports
                anything.
              </Typography>
            </Stack>
          )}
          {deleteError && (
            <Typography sx={{ color: tokens.error, mt: 2, fontSize: 13.5 }}>
              {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={holders !== null && holders.length > 0}
            onClick={() => void doDelete()}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {dialog && (
        // Remounted by key: the remount IS the reset. See [[cms-list-and-editor]].
        <CarouselEditorDialog
          key={dialog.editing?.id ?? "new"}
          open
          initial={dialog.editing}
          saving={saving}
          error={saveError}
          onClose={() => setDialog(null)}
          onSubmit={(draft) => void submit(draft)}
        />
      )}
    </PageContainer>
  );
}
